import { IRootScopeService } from "angular";
import { IModalScope, IModalService } from "angular-ui-bootstrap";
import { IIndicatorErrorOptions, IModalErrorOptions, IHandleIndicatorOptions, IUIError } from "../models";
import { localize } from "@gtmhub/localization";
import { createUIError, logCaughtJsError } from "../util";
import { IAppConfig } from "@gtmhub/env";
import { AnalyticsService } from "@gtmhub/core/analytics";
import { StateService } from "@uirouter/angularjs";

interface IErrorScope extends IModalScope {
  error: IUIError;
  hideBody: boolean;
  translated?: {
    title: string;
    message: string;
  };
}

export class UIErrorHandlingService {
  public static $inject = ["$rootScope", "$state", "$uibModal", "appConfig", "AnalyticsService"];

  constructor(
    private $rootScope: IRootScopeService,
    private $state: StateService,
    private $uibModal: IModalService,
    private appConfig: IAppConfig,
    private analyticsService: AnalyticsService
  ) {}

  public handleIndicator<T>(what: T, indicatorOptions: IIndicatorErrorOptions): void {
    logCaughtJsError(what, this.appConfig);
    const error = createUIError(what);
    const handleIndicatorOptions: IHandleIndicatorOptions = { error, indicatorOptions };
    this.$rootScope.$broadcast("errorReceived", handleIndicatorOptions);
  }

  public handleModal<T>(what: T, modalOptions: IModalErrorOptions = {}): void {
    logCaughtJsError(what, this.appConfig);
    const error = createUIError(what);

    // we broadcast event with no indicator options and check in indicators controller
    // if there are no indicator options --> return;
    const optionsWithErrorOnly: IHandleIndicatorOptions = { error };
    this.$rootScope.$broadcast("errorReceived", optionsWithErrorOnly);

    // if error is false like stop execution and console error it
    if (!error) {
      return;
    }

    // with no connection cannot get html from error.jade
    let template: string;
    if (error.connectionError) {
      template = `
        <style>
          .modal .modal-dialog .modal-content {
            min-height: fit-content;
            min-height: -moz-fit-content;
          }
        </style>
        <div class="gh-empty-module">
          <span class="nc-icon-outline icon ui-1_circle-delete"></span>
          <div class="gh-empty-module-title">{{translated.title}}</div>
          <p>{{translated.message}}</p>
          <p ng-if="error.transactionId">Transaction ID: {{error.transactionId}}</p>
        </div>
      `;
    }

    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const ctrl = this;
    this.$uibModal.open({
      templateUrl: require("./error.jade"),
      template,
      windowClass: modalOptions.windowClass || "medium-small",
      backdrop: typeof modalOptions.backdrop === "undefined" ? true : modalOptions.backdrop,
      controller: [
        "$scope",
        function ($scope: IErrorScope) {
          ctrl.trackError("modal");
          $scope.error = error;
          $scope.hideBody = modalOptions.hideBody;

          if (error.connectionError) {
            $scope.translated = {
              title: localize(error.title),
              message: localize(error.message),
            };
          }
        },
      ],
    });
  }

  public trackError(handleWith: "indicator" | "modal" | "page"): void {
    const stateName = this.$state.current.name;
    const url = this.$state.href(this.$state.current, this.$state.params);
    this.analyticsService.track("Error Shown", { handle_with: handleWith, state_name: stateName, url });
  }
}
